import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';

const CompareControls = ({ onFilterChange }) => {
    const { get } = useApi();
    const [mode, setMode] = useState('term'); // 'term' or 'year'
    const [years, setYears] = useState([]);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedA, setSelectedA] = useState('');
    const [selectedB, setSelectedB] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [yearsData, termsData] = await Promise.all([
                    get('/api/academic-years/'),
                    get('/api/academic-terms/')
                ]);
                setYears(yearsData);
                setTerms(termsData);
            } catch (error) {
                console.error('Error fetching periods:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [get]);

    useEffect(() => {
        // Reset selections when mode changes
        setSelectedA('');
        setSelectedB('');
    }, [mode]);

    useEffect(() => {
        // Only trigger update if both are selected or if we want to reset to default (empty)
        const params = {};
        if (selectedA && selectedB) {
            if (mode === 'year') {
                params.target_year_id = selectedA;
                params.compare_year_id = selectedB;
            } else {
                params.term_id = selectedA;
                params.target_term_id = selectedA;
                params.compare_term_id = selectedB;
            }
        }
        onFilterChange(params);
    }, [selectedA, selectedB, mode, onFilterChange]);

    if (loading) return null;

    return (
        <div className="flex items-center gap-3 bg-slate-50/80 p-1.5 rounded-2xl border border-slate-200 shadow-sm overflow-x-auto scrollbar-hide max-w-full">
            {/* Mode Toggle */}
            <div className="flex bg-white rounded-xl shadow-inner border border-slate-100 p-1 shrink-0">
                <button
                    onClick={() => setMode('term')}
                    className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${mode === 'term' ? 'bg-[#18216D] text-white shadow-md' : 'text-slate-400 hover:text-[#18216D]'}`}
                >Term</button>
                <button
                    onClick={() => setMode('year')}
                    className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${mode === 'year' ? 'bg-[#18216D] text-white shadow-md' : 'text-slate-400 hover:text-[#18216D]'}`}
                >Year</button>
            </div>

            <div className="flex items-center gap-2 px-2 shrink-0">
                {/* Target Selection (Blue) */}
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm bg-[#18216D]"></div>
                    <select
                        value={selectedA}
                        onChange={(e) => setSelectedA(e.target.value)}
                        className="bg-transparent text-xs font-black text-[#18216D] outline-none cursor-pointer hover:text-indigo-600 transition-colors max-w-[120px]"
                    >
                        <option value="">{mode === 'term' ? 'Select Term' : 'Select Year'}</option>
                        {mode === 'year'
                            ? years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)
                            : terms.map(t => <option key={t.id} value={t.id}>{t.name} ({t.year_name})</option>)
                        }
                    </select>
                </div>

                <span className="text-[10px] font-black text-slate-300 uppercase tracking-tighter italic">vs</span>

                {/* Compare Selection (Yellow) */}
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm bg-[#FFC425]"></div>
                    <select
                        value={selectedB}
                        onChange={(e) => setSelectedB(e.target.value)}
                        className="bg-transparent text-xs font-black text-[#18216D] outline-none cursor-pointer hover:text-indigo-600 transition-colors max-w-[120px]"
                    >
                        <option value="">{mode === 'term' ? 'Compare Term' : 'Compare Year'}</option>
                        {mode === 'year'
                            ? years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)
                            : terms.map(t => <option key={t.id} value={t.id}>{t.name} ({t.year_name})</option>)
                        }
                    </select>
                </div>
            </div>
        </div>
    );
};

export default CompareControls;
